/**
 * Base API for Email Service (NestJS)
 *
 * This connects directly to the NestJS email service at VITE_EMAIL_API_URL.
 * Uses the same JWT token as the Django backend for authentication.
 */
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { USER_ACCESS_TOKEN } from '@constants/auth';
import { tokenStorage } from '@utils/tokenStorage';
import { withReauth } from './baseQueryWithReauth';

// Get the email service URL - in dev, use Vite proxy; in prod, use env var
const getEmailServiceUrl = () => {
  if (import.meta.env.DEV) {
    // In development, use relative URL to go through Vite proxy
    return '';
  }
  return import.meta.env.VITE_EMAIL_API_URL || '';
};

const baseQuery = fetchBaseQuery({
  baseUrl: getEmailServiceUrl(),
  prepareHeaders: headers => {
    const token = tokenStorage.getToken(USER_ACCESS_TOKEN);
    if (token) headers.set('Authorization', `Bearer ${token}`);
    return headers;
  },
});

// Tag types for the email service API cache
export const emailServiceTags = [
  'EmailAccount',
  'EmailCampaign',
  'EmailSequence',
  'EmailTemplate',
  'EmailThread',
  'Lead',
  'WarmupProfile',
  'WarmupStats',
  'CustomDomain',
  'Statistics',
  'SharedPoolEmail',
  'PoolConfiguration',
] as const;

export const emptyEmailServiceApi = createApi({
  reducerPath: 'emailServiceApi',
  baseQuery: withReauth(baseQuery),
  tagTypes: emailServiceTags,
  endpoints: () => ({}),
});

export const invalidateAllEmailServiceTags = () => emptyEmailServiceApi.util.invalidateTags([...emailServiceTags]);
